'use client';
import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { Bell, ChevronDown, Package, MapPin, History, User, LogOut, Settings, HelpCircle, Navigation } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { href: '/', label: 'Book Delivery', icon: Package },
  { href: '/track', label: 'Track Order', icon: Navigation },
  { href: '/rider-dashboard', label: 'Rider Portal', icon: MapPin },
  { href: '/admin-dashboard', label: 'Admin', icon: Settings },
];

export default function CustomerTopbar() {
  const pathname = usePathname();
  const router = useRouter();
  const { profile, user, signOut } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [activePath, setActivePath] = useState<string | null>(null);

  useEffect(() => {
    setActivePath(pathname);
  }, [pathname]);

  const displayName = profile?.full_name || user?.user_metadata?.full_name || 'Amina Mwangi';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const notifications = [
    { id: 'notif-1', text: 'Order PK-004821 delivered successfully', time: '5m ago', read: false },
    { id: 'notif-2', text: 'Rider Juma is on the way to pickup', time: '18m ago', read: false },
    { id: 'notif-3', text: 'Your order PK-004819 has been confirmed', time: '1h ago', read: true },
  ];

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/login');
    } catch {
      router.push('/login');
    }
  };

  const userMenuItems = [
    { icon: User, label: 'My Profile', href: '/profile' },
    { icon: History, label: 'Order History', href: '/profile' },
    { icon: Navigation, label: 'Track Delivery', href: '/track' },
    { icon: HelpCircle, label: 'Help & Support', href: null },
    { icon: Settings, label: 'Settings', href: null },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-card border-b border-border">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 shrink-0">
          <AppLogo size={36} />
          <div className="hidden sm:block">
            <span className="font-extrabold text-lg text-foreground leading-tight tracking-tight">Percel Kitaa</span>
            <p className="text-[10px] text-muted-foreground leading-none tracking-wide">Rahisi, Salama na Haraka</p>
          </div>
        </Link>

        {/* Nav Links */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks?.map((link) => {
            const active = activePath === link?.href;
            return (
              <Link
                key={`topnav-${link?.href}`}
                href={link?.href}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ${
                  active
                    ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <link.icon size={15} />
                {link?.label}
              </Link>
            );
          })}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => { setNotifOpen(!notifOpen); setUserMenuOpen(false); }}
              className="relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
              aria-label="Notifications"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-primary text-primary-foreground text-[9px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 card-elevated shadow-card-lg z-50 animate-fade-in overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <span className="font-semibold text-sm text-foreground">Notifications</span>
                  <span className="text-xs text-primary font-medium cursor-pointer hover:underline">Mark all read</span>
                </div>
                <div className="divide-y divide-border">
                  {notifications?.map(n => (
                    <div key={n?.id} className={`px-4 py-3 hover:bg-muted transition-colors cursor-pointer ${!n?.read ? 'bg-primary/5' : ''}`}>
                      <p className={`text-sm ${!n?.read ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{n?.text}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{n?.time}</p>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-2 border-t border-border">
                  <span className="text-xs text-primary font-medium cursor-pointer hover:underline">View all notifications</span>
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => { setUserMenuOpen(!userMenuOpen); setNotifOpen(false); }}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted transition-all duration-150"
            >
              <div className="w-7 h-7 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold">
                {initials}
              </div>
              <span className="hidden sm:block text-sm font-medium text-foreground">{displayName}</span>
              <ChevronDown size={14} className="text-muted-foreground" />
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-52 card-elevated shadow-card-lg z-50 animate-fade-in overflow-hidden py-1">
                {userMenuItems?.map(item => (
                  item.href ? (
                    <Link
                      key={`usermenu-${item?.label}`}
                      href={item.href}
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      <item.icon size={15} className="text-muted-foreground" />
                      {item?.label}
                    </Link>
                  ) : (
                    <button
                      key={`usermenu-${item?.label}`}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      <item.icon size={15} className="text-muted-foreground" />
                      {item?.label}
                    </button>
                  )
                ))}
                <div className="border-t border-border mt-1 pt-1">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={15} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}